// Whole-house ducted configurations built from what the user entered on
// Layout & Sizing rather than on System Design.
//
// Two consumers share the core builder:
//   - the quote-time layout for compatibility (ducted) systems
//     (buildDuctedLayoutConfiguration): the strict ducted path never visits
//     System Design, so the quoted system reaches the quote with a
//     configuration that has no geometry. The layout is built on demand at
//     quote-send time and never enters the configurations slice.
//   - the hybrid whole-house configuration (utils/hybrid-whole-house-
//     configuration): a real configuration kept in the slice while a hybrid
//     project has no mini-split rooms.
// Both take positions from the existing equipment placed on Layout & Sizing
// (outdoor unit → condenser, furnace / coil → air handler) and fall back to
// computed spots when nothing is placed.
import {
  AirHandlerProductType,
  AirHandlerType,
  AirHandlerTypeCodeType,
  CondenserLocationType,
  CondenserPlacementType,
  CondenserProductType,
  CondenserType,
  ExistingSystemComponentType,
  ExistingSystemType,
  LevelType,
  PositionType,
  SpaceType,
  SystemOptionType,
  ZoneItemType,
  ZoneType
} from 'data/types';
import { nanoid } from 'nanoid';
import { readCompatibilityPairs } from 'utils/compatibility-bundle-fields';
import { readExistingSystemUniqueId } from 'utils/compatibility-systems';
import {
  calculateHeightOffGround,
  filterConditionedSpaces
} from 'utils/configurations';
import {
  COMPONENT_TYPE_TO_POSITION,
  ExistingEquipmentPositionSlot,
  isExistingEquipmentComponentPlaced
} from 'utils/existing-equipment';
import { partitionLayoutLevels } from 'utils/ducted-layout-level-partition';
import { defaultAirHandlerPosition } from 'utils/hybrid-config-air-handlers';
import { hybridLevelBoundingBox } from 'utils/hybrid-config-spaces';
import { snapAutoPlacedCondenser } from 'utils/auto-condenser-placement';
import { isLevelConditioned } from 'utils/level-conditioning';
import { WholeHouseDuctedConfigurationType } from 'utils/whole-house-ducted-configuration-types';

// Existing-equipment grid objects render 4x4 tiles (see
// getExistingEquipmentGridObjects) while configuration condensers and air
// handlers render 3x3, so a synthetic unit sits half a tile in from the
// object's top-left corner to share its center.
const EXISTING_EQUIPMENT_TILE_SIZE = 4;
const CONFIGURATION_UNIT_TILE_SIZE = 3;
const CENTERING_OFFSET_TILES =
  (EXISTING_EQUIPMENT_TILE_SIZE - CONFIGURATION_UNIT_TILE_SIZE) / 2;

// Unplaced outdoor unit: park it just outside the level's footprint, the same
// spot the hybrid auto-build picks for its condensers.
const FALLBACK_CONDENSER_GAP_FT = 1;
const DEFAULT_CONDENSER_SIDE: CondenserLocationType = 'right';

// Furnaces / ducted air handlers are the 'ducted' air handler type; the
// configuration is marked so the duct layout builder includes those zones.
const LAYOUT_AIR_HANDLER_TYPE_CODE: AirHandlerTypeCodeType = 'ducted';

export type DuctedLayoutSpace = {
  space: SpaceType;
  level: LevelType;
};

export type WholeHouseDuctedIds = {
  configurationUniqueId: string;
  condenserUniqueId: string;
  zoneUniqueId: string;
  airHandlerUniqueId?: string;
};

export type WholeHouseDuctedNames = {
  configuration: string;
  condenser: string;
  zone: string;
};

export type WholeHouseDuctedProducts = {
  condenser?: CondenserProductType;
  zone?: AirHandlerProductType;
};

// 'ducted' is the compat quote layout (drawn only); 'hybrid' is the shape the
// valid-systems fetch needs for a live configuration — a 'ducted' condenser
// suppresses the zone's requiresDuctedAirHandler flag
// (shouldRequireDuctedAirHandler), so a hybrid-catalog fetch must not use it.
export type WholeHouseDuctedType = 'ducted' | 'hybrid';

export type BuildWholeHouseDuctedConfigurationParams = {
  ids: WholeHouseDuctedIds;
  names: WholeHouseDuctedNames;
  type: WholeHouseDuctedType;
  existingSystem: ExistingSystemType | null;
  structure: LevelType[];
  products?: WholeHouseDuctedProducts;
  // The Layout & Sizing "Condenser Locations" choice; the first selected side
  // places an unplaced outdoor unit.
  condenserLocations?: readonly CondenserLocationType[] | null;
  // Zone item uniqueIds to keep per space (structureItemId → uniqueId) so a
  // regenerated configuration stays stable for its consumers.
  zoneItemUniqueIdsBySpaceId?: ReadonlyMap<string, string>;
  levelUniqueIds?: ReadonlySet<string>;
};

export type BuildDuctedLayoutConfigurationParams = {
  system: SystemOptionType;
  existingSystem: ExistingSystemType | null;
  structure: LevelType[];
  existingSystems?: ExistingSystemType[];
};

type CondenserPlacement = {
  position: PositionType;
  placementType: CondenserPlacementType;
};

function hasRenderableGeometry(space: SpaceType): boolean {
  if (!space.position) return false;
  if ((space.vertices?.length ?? 0) >= 3) return true;
  return !!space.width && !!space.length;
}

// Whole-house zone (the ducted precedent in createDuctedConfigWithReplacement
// Systems): every conditioned space on a conditioned level that the diagram
// can actually draw. Sizing is deliberately not required — tonnage-skip
// projects have unsized rooms that still deserve an outline.
export function collectDuctedLayoutSpaces(
  structure: LevelType[]
): DuctedLayoutSpace[] {
  return structure
    .filter((level) => isLevelConditioned(level))
    .flatMap((level) =>
      filterConditionedSpaces(
        level.sections.flatMap((section) => section.spaces)
      )
        .filter(hasRenderableGeometry)
        .map((space) => ({ space, level }))
    );
}

function findPlacedComponent(
  existingSystem: ExistingSystemType | null,
  slot: ExistingEquipmentPositionSlot
): ExistingSystemComponentType | null {
  if (!existingSystem) return null;
  return (
    existingSystem.components.find(
      (component) =>
        COMPONENT_TYPE_TO_POSITION[component.type] === slot &&
        isExistingEquipmentComponentPlaced(component)
    ) ?? null
  );
}

function centeredPosition(
  component: ExistingSystemComponentType
): PositionType {
  return {
    x: (component.position?.x ?? 0) + CENTERING_OFFSET_TILES,
    y: (component.position?.y ?? 0) + CENTERING_OFFSET_TILES
  };
}

// Corner-anchored just outside the level's bounding box on the chosen side
// ('right' keeps the hybrid auto-build's top-right spot); 'roof' sits on the
// footprint itself.
function fallbackGroundPosition(
  bbox: NonNullable<ReturnType<typeof hybridLevelBoundingBox>>,
  side: CondenserLocationType
): PositionType {
  const outsideOffset =
    FALLBACK_CONDENSER_GAP_FT + CONFIGURATION_UNIT_TILE_SIZE;
  switch (side) {
    case 'left':
      return { x: bbox.minX - outsideOffset, y: bbox.minY };
    case 'top':
      return { x: bbox.minX, y: bbox.minY - outsideOffset };
    case 'bottom':
      return { x: bbox.minX, y: bbox.maxY + FALLBACK_CONDENSER_GAP_FT };
    case 'right':
    default:
      return { x: bbox.maxX + FALLBACK_CONDENSER_GAP_FT, y: bbox.minY };
  }
}

function fallbackCondenserPlacement(
  level: LevelType,
  spaces: SpaceType[],
  side: CondenserLocationType
): CondenserPlacement {
  const bbox = hybridLevelBoundingBox(level, spaces);
  if (!bbox) return { position: { x: 0, y: 0 }, placementType: 'ground' };
  if (side === 'roof') {
    return {
      position: {
        x: (bbox.minX + bbox.maxX - CONFIGURATION_UNIT_TILE_SIZE) / 2,
        y: (bbox.minY + bbox.maxY - CONFIGURATION_UNIT_TILE_SIZE) / 2
      },
      placementType: 'roof'
    };
  }
  return {
    position: snapAutoPlacedCondenser(
      fallbackGroundPosition(bbox, side),
      level,
      spaces,
      CONFIGURATION_UNIT_TILE_SIZE,
      FALLBACK_CONDENSER_GAP_FT
    ),
    placementType: 'ground'
  };
}

function zoneItemsForSpaces(
  spaces: SpaceType[],
  zoneItemUniqueIdsBySpaceId?: ReadonlyMap<string, string>
): ZoneItemType[] {
  return spaces.map((space) => ({
    uniqueId: zoneItemUniqueIdsBySpaceId?.get(space.uniqueId) ?? nanoid(5),
    structureItemId: space.uniqueId,
    name: space.label,
    BTUs: space.BTUs,
    originalBTUs: space.BTUs
  }));
}

// The indoor unit's placed position is the duct-run origin: userPositioned
// is what getConcealedDuctOriginOverridePx honors. Unplaced indoor units keep
// userPositioned off so the layout computes its area-weighted default origin.
function buildLayoutAirHandler(
  uniqueId: string,
  indoorComponent: ExistingSystemComponentType | null,
  anchorSpace: SpaceType
): AirHandlerType {
  const base = {
    uniqueId,
    airHandlerTypeCode: LAYOUT_AIR_HANDLER_TYPE_CODE,
    valid: true,
    errorMessage: null,
    warningMessage: null,
    modified: false
  };
  if (!indoorComponent) {
    return {
      ...base,
      ...defaultAirHandlerPosition(anchorSpace),
      userPositioned: false
    };
  }
  const position = centeredPosition(indoorComponent);
  return {
    ...base,
    positionX: position.x,
    positionY: position.y,
    levelUniqueId: indoorComponent.levelUniqueId,
    userPositioned: true
  };
}

// Which existing system's placed equipment positions a whole-house layout
// should use when nothing points at one: the only ducted system with
// components, else the first ducted system with anything placed. Null means
// "no positions — fall back to computed spots".
export function resolveExistingSystemForLayout(
  existingSystems: ExistingSystemType[]
): ExistingSystemType | null {
  const ductedWithComponents = existingSystems.filter(
    (s) => s.type === 'ducted' && s.components.length > 0
  );
  if (ductedWithComponents.length === 1) return ductedWithComponents[0];

  return (
    ductedWithComponents.find((s) =>
      s.components.some(isExistingEquipmentComponentPlaced)
    ) ?? null
  );
}

// A quoted compatibility system prefers the stamp written when it was
// manufactured, then the shared heuristic.
export function resolveLayoutExistingSystem(
  system: SystemOptionType,
  existingSystems: ExistingSystemType[]
): ExistingSystemType | null {
  const stampedUniqueId = readExistingSystemUniqueId(system);
  const stamped = stampedUniqueId
    ? existingSystems.find((s) => s.uniqueId === stampedUniqueId)
    : undefined;
  return stamped ?? resolveExistingSystemForLayout(existingSystems);
}

export function buildWholeHouseDuctedConfiguration({
  ids,
  names,
  type,
  existingSystem,
  structure,
  products,
  condenserLocations,
  zoneItemUniqueIdsBySpaceId,
  levelUniqueIds
}: BuildWholeHouseDuctedConfigurationParams): WholeHouseDuctedConfigurationType | null {
  const layoutSpaces = collectDuctedLayoutSpaces(structure).filter(
    (entry) => !levelUniqueIds || levelUniqueIds.has(entry.level.uniqueId)
  );
  if (layoutSpaces.length === 0) return null;
  const zonedLevelIds = new Set(
    layoutSpaces.map((entry) => entry.level.uniqueId)
  );

  const outdoorComponent = findPlacedComponent(existingSystem, 'outdoor');
  const indoorComponent =
    findPlacedComponent(existingSystem, 'blower') ??
    findPlacedComponent(existingSystem, 'indoor');

  const anchorLevel =
    layoutSpaces.find(
      (entry) => entry.level.uniqueId === indoorComponent?.levelUniqueId
    )?.level ?? layoutSpaces[0].level;
  const anchorLevelSpaces = layoutSpaces
    .filter((entry) => entry.level.uniqueId === anchorLevel.uniqueId)
    .map((entry) => entry.space);

  // A condenser only renders on the customer page when its level has zoned
  // spaces, so an outdoor unit parked on an unzoned level (unconditioned
  // basement) drops the level and lands on the diagram's default level.
  const outdoorLevelUniqueId =
    outdoorComponent?.levelUniqueId &&
    zonedLevelIds.has(outdoorComponent.levelUniqueId)
      ? outdoorComponent.levelUniqueId
      : undefined;
  const condenserLevelUniqueId = outdoorComponent
    ? outdoorLevelUniqueId
    : anchorLevel.uniqueId;

  const placement: CondenserPlacement = outdoorComponent
    ? { position: centeredPosition(outdoorComponent), placementType: 'ground' }
    : fallbackCondenserPlacement(
        anchorLevel,
        anchorLevelSpaces,
        condenserLocations?.[0] ?? DEFAULT_CONDENSER_SIDE
      );

  const zone: ZoneType = {
    uniqueId: ids.zoneUniqueId,
    name: names.zone,
    type: 'ducted',
    ...(existingSystem ? { existingSystemId: existingSystem.uniqueId } : {}),
    zoneItems: zoneItemsForSpaces(
      layoutSpaces.map((entry) => entry.space),
      zoneItemUniqueIdsBySpaceId
    ),
    ...(products?.zone ? { selectedProduct: products.zone } : {}),
    airHandler: buildLayoutAirHandler(
      ids.airHandlerUniqueId ?? nanoid(5),
      indoorComponent,
      anchorLevelSpaces[0]
    ),
    modified: false
  };

  const condenser: CondenserType = {
    uniqueId: ids.condenserUniqueId,
    type,
    name: names.condenser,
    ...(condenserLevelUniqueId
      ? { levelUniqueId: condenserLevelUniqueId }
      : {}),
    position: placement.position,
    placementType: placement.placementType,
    heightOffGround: calculateHeightOffGround(
      placement.placementType,
      condenserLevelUniqueId,
      structure
    ),
    ...(products?.condenser ? { selectedProduct: products.condenser } : {}),
    zones: [zone],
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    modified: false
  };

  return {
    uniqueId: ids.configurationUniqueId,
    name: names.configuration,
    type,
    condensers: [condenser],
    valid: true,
    modified: false,
    layOutDuctedZones: true
  };
}

function resolvePairExistingSystem(
  system: SystemOptionType,
  condenserUniqueId: string,
  existingSystem: ExistingSystemType | null,
  existingSystems: ExistingSystemType[]
): ExistingSystemType | null {
  const pair = readCompatibilityPairs(system).find(
    (candidate) => candidate.condenserUniqueId === condenserUniqueId
  );
  const paired = pair?.existingSystemUniqueId
    ? existingSystems.find((s) => s.uniqueId === pair.existingSystemUniqueId)
    : undefined;
  return paired ?? existingSystem;
}

export function resolveLayoutExistingSystems(
  system: SystemOptionType,
  existingSystems: ExistingSystemType[]
): (ExistingSystemType | null)[] {
  const shared = resolveLayoutExistingSystem(system, existingSystems);
  return system.configuration.condensers.map((condenser) =>
    resolvePairExistingSystem(
      system,
      condenser.uniqueId,
      shared,
      existingSystems
    )
  );
}

// Compatibility systems are manufactured as one condenser with one zone per
// pair (assembleCompatibilitySelectedSystem); the layout mirrors that shape
// and reuses its uniqueIds so the snapshot's condensers and duct layouts line
// up with the quoted configuration on the customer page. Several pairs split
// the house by level (partitionLayoutLevels).
export function buildDuctedLayoutConfiguration({
  system,
  existingSystem,
  structure,
  existingSystems = []
}: BuildDuctedLayoutConfigurationParams): WholeHouseDuctedConfigurationType | null {
  const sources = system.configuration.condensers.filter(
    (condenser) => condenser.zones.length > 0
  );
  if (sources.length === 0) return null;

  const pairSystems = sources.map((condenser) =>
    resolvePairExistingSystem(
      system,
      condenser.uniqueId,
      existingSystem,
      existingSystems
    )
  );
  const levelPartitions = partitionLayoutLevels(pairSystems, structure);

  const condensers = sources.map((sourceCondenser, index) => {
    const sourceZone = sourceCondenser.zones[0];
    const built = buildWholeHouseDuctedConfiguration({
      ids: {
        configurationUniqueId: system.configuration.uniqueId,
        condenserUniqueId: sourceCondenser.uniqueId,
        zoneUniqueId: sourceZone.uniqueId
      },
      names: {
        configuration: `${system.name} Layout`,
        condenser: sourceCondenser.name,
        zone: sourceZone.name
      },
      type: 'ducted',
      existingSystem: pairSystems[index],
      structure,
      products: {
        condenser: sourceCondenser.selectedProduct,
        zone: sourceZone.selectedProduct
      },
      levelUniqueIds: levelPartitions[index]
    });
    return built?.condensers[0] ?? null;
  });
  const placed = condensers.filter(
    (condenser): condenser is CondenserType => condenser !== null
  );
  if (placed.length === 0) return null;

  return {
    uniqueId: system.configuration.uniqueId,
    name: `${system.name} Layout`,
    type: 'ducted',
    condensers: placed,
    valid: true,
    modified: false,
    layOutDuctedZones: true
  };
}
